import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, Filter, Search, ArrowLeft, Edit, FileText, LayoutGrid, CheckSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface ActionButton {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  href?: string;
}

interface MobileActionBarConfig {
  leftAction?: ActionButton;
  rightAction?: ActionButton;
  centerAction: {
    icon: LucideIcon;
    label: string;
    href: string;
  };
}

const MobileActionBar: React.FC = () => {
  const location = useLocation();

  // Helper functions for common actions
  const scrollToFilters = () => {
    const filtersSection = document.getElementById('mobile-filters-section');
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      filtersSection.classList.add('animate-pulse');
      setTimeout(() => filtersSection.classList.remove('animate-pulse'), 1000);
    }
  };

  const scrollToSearch = () => {
    const searchSection = document.getElementById('mobile-search-section');
    const searchInput = searchSection?.querySelector('input');
    if (searchSection && searchInput) {
      searchSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => searchInput.focus(), 500);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Page-specific configurations
  const getPageConfig = (): MobileActionBarConfig | null => {
    const path = location.pathname;

    // Hide on these routes (same as FloatingActionButton)
    if (path.startsWith('/places') || path.startsWith('/settings')) {
      return null;
    }

    // Leads list page (/app)
    if (path === '/app') {
      return {
        leftAction: { icon: Filter, label: 'Filters', onClick: scrollToFilters },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
      };
    }

    // Tasks page
    if (path === '/tasks') {
      return {
        leftAction: { icon: Filter, label: 'Filters', onClick: scrollToFilters },
        rightAction: { icon: LayoutGrid, label: 'View', onClick: scrollToTop },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
      };
    }

    // Lead details page
    if (path.startsWith('/leads/')) {
      return {
        leftAction: { icon: ArrowLeft, label: 'Back', onClick: goBack },
        rightAction: { icon: FileText, label: 'Notes', onClick: () => {
          window.location.hash = 'notes';
          scrollToTop();
        }},
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
      };
    }

    // Dashboard page
    if (path === '/dashboard') {
      return {
        leftAction: { icon: LayoutGrid, label: 'View', onClick: scrollToTop },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
      };
    }

    // Companies page
    if (path === '/companies') {
      return {
        leftAction: { icon: Filter, label: 'Filter', onClick: scrollToFilters },
        rightAction: { icon: Search, label: 'Search', onClick: scrollToSearch },
        centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
      };
    }

    // Profile and other pages - just center action
    return {
      centerAction: { icon: Plus, label: 'Add Lead', href: '/places' }
    };
  };

  const config = getPageConfig();

  // Don't render if no config (hidden routes)
  if (!config) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
      {/* Glassmorphic Bar with Blur Effect */}
      <div className="backdrop-blur-lg bg-background/80 border-t border-border/40 shadow-2xl">
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent pointer-events-none" />
        
        <nav className="relative flex items-center justify-between h-16 max-w-screen-xl mx-auto px-6 safe-area-inset-bottom">
          {/* Left Action */}
          <div className="flex-1 flex justify-start">
            {config.leftAction ? (
              config.leftAction.href ? (
                <Link
                  to={config.leftAction.href}
                  aria-label={config.leftAction.label}
                  className="group relative flex items-center justify-center h-11 w-11 rounded-full bg-muted/80 hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
                >
                  <config.leftAction.icon className="h-5 w-5 text-foreground transition-transform group-hover:rotate-12 duration-300" />
                  <div className="absolute inset-0 rounded-full border-2 border-primary/20 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300" />
                </Link>
              ) : (
                <button
                  onClick={config.leftAction.onClick}
                  aria-label={config.leftAction.label}
                  className="group relative flex items-center justify-center h-11 w-11 rounded-full bg-muted/80 hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
                >
                  <config.leftAction.icon className="h-5 w-5 text-foreground transition-transform group-hover:rotate-12 duration-300" />
                  <div className="absolute inset-0 rounded-full border-2 border-primary/20 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300" />
                </button>
              )
            ) : <div />}
          </div>
          
          {/* Center: Premium FAB */}
          <div className="flex items-center justify-center">
            {/* Outer glow ring */}
            <div className="absolute h-16 w-16 rounded-full bg-primary/20 blur-xl animate-pulse-glow -mt-8" />
            
            {/* Main FAB */}
            <Link 
              to={config.centerAction.href}
              aria-label={config.centerAction.label}
              className="relative -mt-10 group"
            >
              <div className="relative h-14 w-14 rounded-full bg-gradient-to-br from-primary via-primary to-primary/80 shadow-2xl transform transition-all duration-300 hover:scale-110 active:scale-95 hover:shadow-primary/50 flex items-center justify-center">
                {/* Inner gradient highlight */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/20 to-transparent opacity-50" />
                
                {/* Center icon */}
                <config.centerAction.icon className="h-6 w-6 text-white relative z-10 transition-transform group-hover:rotate-90 duration-300" />
                
                {/* Pulse ring on hover */}
                <div className="absolute inset-0 rounded-full border-2 border-primary/30 scale-110 opacity-0 group-hover:scale-125 group-hover:opacity-100 transition-all duration-500" />
              </div>
            </Link>
          </div>
          
          {/* Right Action */}
          <div className="flex-1 flex justify-end">
            {config.rightAction ? (
              config.rightAction.href ? (
                <Link
                  to={config.rightAction.href}
                  aria-label={config.rightAction.label}
                  className="group relative flex items-center justify-center h-11 w-11 rounded-full bg-muted/80 hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
                >
                  <config.rightAction.icon className="h-5 w-5 text-foreground transition-transform group-hover:scale-110 duration-300" />
                  <div className="absolute inset-0 rounded-full border-2 border-primary/20 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300" />
                </Link>
              ) : (
                <button
                  onClick={config.rightAction.onClick}
                  aria-label={config.rightAction.label}
                  className="group relative flex items-center justify-center h-11 w-11 rounded-full bg-muted/80 hover:bg-muted transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
                >
                  <config.rightAction.icon className="h-5 w-5 text-foreground transition-transform group-hover:scale-110 duration-300" />
                  <div className="absolute inset-0 rounded-full border-2 border-primary/20 scale-100 opacity-0 group-hover:scale-110 group-hover:opacity-100 transition-all duration-300" />
                </button>
              )
            ) : <div />}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MobileActionBar;
